// 📁 src/context/IdeaContext.js
import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "../utils/axiosInstance";
import { useAuth } from "./AuthContext";

const IdeaContext = createContext();

export const IdeaProvider = ({ children }) => {
  const { user } = useAuth();

  const [userId, setUserId] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const [selectedIdea, setSelectedIdea] = useState(null);

  /* --------------------------------------------------------
     1) Reset immediately on logout
  -------------------------------------------------------- */
  useEffect(() => {
    if (!user) {
      setSelectedIdea(null);
      localStorage.removeItem("selectedIdea");
      setUserId(null);
      setInitializing(false);
      return;
    }
    setUserId(user._id);
  }, [user]);

  /* --------------------------------------------------------
     2) Only restore from localStorage if same user
  -------------------------------------------------------- */
  useEffect(() => {
    if (!user?._id) return;

    const saved = localStorage.getItem("selectedIdea");
    if (!saved) return;

    try {
      const parsed = JSON.parse(saved);
      if (parsed?.userId === user._id) {
        setSelectedIdea(parsed);
      } else {
        localStorage.removeItem("selectedIdea"); // 🔥 prevent leak
      }
    } catch {
      localStorage.removeItem("selectedIdea");
    }
  }, [user]);

  /* --------------------------------------------------------
     3) Auto-resume from backend ONLY if nothing restored
  -------------------------------------------------------- */
  useEffect(() => {
    const fetchResume = async () => {
      if (!user?._id) {
        setInitializing(false);
        return;
      }
      if (selectedIdea) {
        setInitializing(false);
        return;
      }

      try {
        const res = await axios.get("/api/idea-generation/latest");
        if (res.data?.success && res.data.idea?.ideaTitle) {
          const idea = res.data.idea;
          const formatted = {
            ideaId: idea.recordId,
            userId: user._id,
            title: idea.ideaTitle,
            description: idea.description,
            problem: idea.problem,
            solution: idea.solution,
            targetUser: idea.targetUser,
            industry: idea.industry,
            createdAt: idea.createdAt,
          };
          setSelectedIdea(formatted);
          localStorage.setItem("selectedIdea", JSON.stringify(formatted));
        }
      } catch {
        /* ignore */
      } finally {
        setInitializing(false);
      }
    };
    fetchResume();
  }, [user, selectedIdea]);

  /* --------------------------------------------------------
     4) Save to storage always with userId
  -------------------------------------------------------- */
  useEffect(() => {
    if (selectedIdea && userId) {
      localStorage.setItem(
        "selectedIdea",
        JSON.stringify({ ...selectedIdea, userId })
      );
    }
  }, [selectedIdea, userId]);

  /* --------------------------------------------------------
     STATES BELOW ARE UNTOUCHED
  -------------------------------------------------------- */
  const [mvpPlanRecordId, setMvpPlanRecordId] = useState(null);
  const [mvpAgentRecordId, setMvpAgentRecordId] = useState(null);
  const [businessModelRecordId, setBusinessModelRecordId] = useState(null);
  const [gtmRecordId, setGTMRecordId] = useState(null);
  const [gtmIdeaId, setGTMIdeaId] = useState(null);
  const [autosavePendingGTM, setAutosavePendingGTM] = useState(false);
  const [pitchDeckRecordId, setPitchDeckRecordId] = useState(null);
  const [fundingMatchRecordId, setFundingMatchRecordId] = useState(null);

  const [marketResearch, setMarketResearch] = useState(null);
  const [mvpDetails, setMvpDetails] = useState(null);
  const [mvpAgentPlan, setMvpAgentPlan] = useState(null);
  const [businessModel, setBusinessModel] = useState(null);
  const [gtmPlan, setGtmPlan] = useState(null);
  const [latestPitchDeck, setLatestPitchDeck] = useState(null);
  const [fundingMatch, setFundingMatch] = useState(null);

  const commitSelectedIdea = (parentRecordId, ideaData) => {
    const finalIdea = {
      ideaId: parentRecordId,
      userId,
      title: ideaData.title || "",
      description: ideaData.description || "",
      problem: ideaData.problem || "",
      solution: ideaData.solution || "",
      targetUser: ideaData.targetUser || "",
      industry: ideaData.industry || "",
      createdAt: new Date().toISOString(),
    };

    setSelectedIdea(finalIdea);
    localStorage.setItem("selectedIdea", JSON.stringify(finalIdea));

    setMvpPlanRecordId(null);
    setMvpAgentRecordId(null);
    setBusinessModelRecordId(null);
    setGTMRecordId(null);
    setGTMIdeaId(null);
    setPitchDeckRecordId(null);
    setFundingMatchRecordId(null);
    setMvpDetails(null);
    setMvpAgentPlan(null);
    setBusinessModel(null);
    setGtmPlan(null);
    setLatestPitchDeck(null);
    setFundingMatch(null);
  };

  const markModuleComplete = (moduleName, data) => {
    switch (moduleName) {
      case "marketResearch": setMarketResearch(data); break;
      case "mvpPlanner": setMvpDetails(data); break;
      case "mvpAgent": setMvpAgentPlan(data); break;
      case "businessModel": setBusinessModel(data); break;
      case "gtmAccelerator": setGtmPlan(data); break;
      case "pitchDeck": setLatestPitchDeck(data); break;
      case "fundingMatch": setFundingMatch(data); break;
    }
  };

  return (
    <IdeaContext.Provider
      value={{
        userId,
        setUserId,
        selectedIdea,
        setSelectedIdea,
        initializing, // modules must wait until resume ends

        commitSelectedIdea,

        mvpPlanRecordId, setMvpPlanRecordId,
        mvpAgentRecordId, setMvpAgentRecordId,
        businessModelRecordId, setBusinessModelRecordId,
        gtmRecordId, setGTMRecordId,
        gtmIdeaId, setGTMIdeaId,
        autosavePendingGTM, setAutosavePendingGTM,
        pitchDeckRecordId, setPitchDeckRecordId,
        fundingMatchRecordId, setFundingMatchRecordId,
        marketResearch, setMarketResearch,
        mvpDetails, setMvpDetails,
        mvpAgentPlan, setMvpAgentPlan,
        businessModel, setBusinessModel,
        gtmPlan, setGtmPlan,
        latestPitchDeck, setLatestPitchDeck,
        fundingMatch, setFundingMatch,
        markModuleComplete,
      }}
    >
      {children}
    </IdeaContext.Provider>
  );
};

export const useIdea = () => useContext(IdeaContext);
